/**
 * Access gate for the in-progress UI revamp.
 *
 * The new UI lives entirely under /v2 and is visible ONLY to the allowlisted
 * accounts below. Everything outside src/newui/ is untouched, so the shipped app
 * behaves exactly as before for every other user — this is a parallel surface,
 * not a migration.
 *
 * Deliberately a client-side allowlist rather than a backend flag: the revamp
 * changes no functionality and needs no new API, and a hardcoded list of two
 * internal accounts is easier to reason about (and to delete) than a new
 * entitlement field. Revisit if this ever gates something a customer pays for —
 * a determined user can read this list, they just can't do anything with it,
 * because /v2 calls the same permission-gated endpoints as /v1.
 */

export const NEW_UI_ALLOWLIST: readonly string[] = [
  'isaac@amplifye.tech',
  'support@flashtickets.fm',
];

/** Where a non-allowlisted visitor to /v2 gets sent. */
export const NEW_UI_FALLBACK_ROUTE = '/notifications';

/**
 * Switchover: v2 is now the shipped UI for every account, so the allowlist is
 * retired and this returns true unconditionally. It must NOT depend on email —
 * admin impersonation sessions carry no Firebase user (email is null), and a
 * false here would bounce them to a legacy route that redirects straight back,
 * looping forever. Restore the allowlist body below to re-gate v2.
 */
export function isNewUiUser(_email?: string | null): boolean {
  return true;
}

/**
 * Whether the bare `/v2` marketing landing page renders for signed-out visitors.
 *
 * `true`: the landing is public. It is now the site's front door — mounted at
 * `/` by `PublicLanding` — so gating the `/v2` copy of the same page would only
 * produce two URLs that disagree about who may read identical marketing copy.
 *
 * This is a deliberate narrowing of the brief's "gated to two accounts" promise,
 * and it is safe to narrow because the landing is inert: it renders no user
 * data, imports nothing from app code, and makes no API call. Everything under
 * `/v2/app` stays gated to `NEW_UI_ALLOWLIST` regardless of this constant, so
 * the revamped *application* is exactly as private as it was.
 */
export const NEW_UI_PUBLIC_LANDING = true;
